// On-device store para busca local de mensagens do chat.
// Cacheia mensagens já descriptografadas no IndexedDB e faz busca por
// substring normalizada (case/acento-insensitive). Sem envolvimento do servidor.

export interface StoredMessage {
  id: number
  conversation_id: number
  kind: string
  author_user_id: number | null
  body: string | null
  created_at: string
  edited_at: string | null
  deleted_at: string | null
}

export interface SyncMeta {
  last_cursor: number
  last_changed_at: string | null
}

const DB_NAME = 'consoleai-chat'
const DB_VERSION = 1
const STORE_MESSAGES = 'messages'
const STORE_SYNC = 'sync_meta'
const SYNC_KEY = 'sync'
const MAX_RESULTS = 100

interface SyncRecord {
  k: string
  v: SyncMeta
}

/** NFD + remove diacríticos + lowercase. "transação" -> "transacao", "Olá" -> "ola". */
function normalize(s: string): string {
  return s.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase()
}

let dbPromise: Promise<IDBDatabase> | null = null

function openDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise
  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE_MESSAGES)) {
        const store = db.createObjectStore(STORE_MESSAGES, { keyPath: 'id' })
        store.createIndex('by_conversation', 'conversation_id', { unique: false })
      }
      if (!db.objectStoreNames.contains(STORE_SYNC)) {
        db.createObjectStore(STORE_SYNC, { keyPath: 'k' })
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
  return dbPromise
}

function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
    tx.onabort = () => reject(tx.error)
  })
}

function reqResult<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

export const messageStore = {
  /** Upsert por id, em uma única transação readwrite. */
  async put(msgs: StoredMessage[]): Promise<void> {
    if (msgs.length === 0) return
    const db = await openDB()
    const tx = db.transaction(STORE_MESSAGES, 'readwrite')
    const store = tx.objectStore(STORE_MESSAGES)
    for (const m of msgs) store.put(m)
    await txDone(tx)
  },

  async remove(id: number): Promise<void> {
    const db = await openDB()
    const tx = db.transaction(STORE_MESSAGES, 'readwrite')
    tx.objectStore(STORE_MESSAGES).delete(id)
    await txDone(tx)
  },

  /** Limpa mensagens E sync_meta. */
  async clear(): Promise<void> {
    const db = await openDB()
    const tx = db.transaction([STORE_MESSAGES, STORE_SYNC], 'readwrite')
    tx.objectStore(STORE_MESSAGES).clear()
    tx.objectStore(STORE_SYNC).clear()
    await txDone(tx)
  },

  async getSyncMeta(): Promise<SyncMeta | null> {
    const db = await openDB()
    const tx = db.transaction(STORE_SYNC, 'readonly')
    const rec = await reqResult<SyncRecord | undefined>(
      tx.objectStore(STORE_SYNC).get(SYNC_KEY),
    )
    return rec ? rec.v : null
  },

  async setSyncMeta(v: SyncMeta): Promise<void> {
    const db = await openDB()
    const tx = db.transaction(STORE_SYNC, 'readwrite')
    tx.objectStore(STORE_SYNC).put({ k: SYNC_KEY, v } satisfies SyncRecord)
    await txDone(tx)
  },

  async search(query: string, opts?: { conversationId?: number }): Promise<StoredMessage[]> {
    const q = normalize(query.trim())
    if (!q) return []
    const db = await openDB()
    const tx = db.transaction(STORE_MESSAGES, 'readonly')
    const all = await reqResult<StoredMessage[]>(tx.objectStore(STORE_MESSAGES).getAll())
    const results = all.filter((m) => {
      if (m.deleted_at) return false
      if (m.kind === 'system') return false
      if (!m.body) return false
      if (opts?.conversationId != null && m.conversation_id !== opts.conversationId) return false
      return normalize(m.body).includes(q)
    })
    results.sort((a, b) => (a.created_at < b.created_at ? 1 : a.created_at > b.created_at ? -1 : 0))
    return results.slice(0, MAX_RESULTS)
  },
}
